import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import type { ReactNode } from "react";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { Spinner } from "@/components/ui/Spinner";
import { useFloatingPanel, estimateAutocompletePanelHeight, floatingPanelStyle } from "@/lib/useFloatingPanel";

export type { AutocompleteOption };

export interface LockedAutocompleteProps<T> {
  label?: ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  /** Aktuell ausgewählter Wert (kontrolliert). */
  value: AutocompleteOption<T> | null;
  /** Wird beim Auswählen oder Löschen aufgerufen. */
  onChange: (next: AutocompleteOption<T> | null) => void;
  /**
   * Wird bei jeder Änderung des (noch nicht übernommenen) Suchtexts aufgerufen.
   * Nützlich, um bei `allowFreeText` den aktuell getippten Text abzugreifen,
   * bevor er per Blur/Enter übernommen wird (z. B. Flush vor dem Speichern).
   */
  onQueryChange?: (query: string) => void;
  /** Datenquelle. Wird debounced aufgerufen. */
  loadOptions: (query: string) => Promise<AutocompleteOption<T>[]>;
  /** Optional: minimale Eingabelänge, ab der gesucht wird (Default 0). */
  minChars?: number;
  /** Optional: Debounce in ms (Default 200). */
  debounceMs?: number;
  /** Anzeigentext, wenn nichts gefunden wird. */
  emptyText?: string;
  className?: string;
  required?: boolean;
  /** Pflichtfehler nach unten reichen. */
  error?: ReactNode;
  /** Verhindert Eingabe + Löschen (z. B. „Kein Kunde am Ordner verknüpft"). */
  disabled?: boolean;
  /** Optionaler Hinweis wenn deaktiviert (z. B. „Bitte zuerst Kunden wählen"). */
  disabledHint?: ReactNode;
  /** Wenn `true`, wird nach dem Mounten das Eingabefeld fokussiert. */
  autoFocus?: boolean;
  /** Beim Auswählen automatisch alle Listen neu laden (Cache umgehen). */
  reloadKey?: string | number;
  /**
   * Liste nach Auswahl offen lassen (Mehrfachauswahl, z. B. Mitarbeiter-Chips).
   * Sinnvoll mit `value={null}` und externer Werteliste.
   */
  keepOpenOnSelect?: boolean;
  /**
   * Freitext ohne Listenauswahl übernehmen (Blur/Enter).
   * Typisch für Mitarbeiter- oder Materialfelder mit manueller Eingabe.
   */
  allowFreeText?: boolean;
  /** Option für freien Text (Default: label = query). */
  createFreeTextOption?: (query: string) => AutocompleteOption<T>;
  /** Gesperrte Auswahl wieder bearbeitbar machen (z. B. Freitext, nicht Hero-Artikel). */
  allowEditLocked?: (option: AutocompleteOption<T>) => boolean;
}

export interface LockedAutocompleteHandle {
  /** Setzt das Feld auf "leer" und gibt dem Eingabefeld den Fokus zurück. */
  clear: () => void;
  /** Fokussiert das Eingabefeld (oder den Clear-Button). */
  focus: () => void;
  /** Aktueller Suchtext im Eingabemodus (z. B. vor dem Speichern eines Dialogs). */
  getPendingQuery: () => string;
}

/**
 * Autocomplete-Komponente, deren Auswahl nach dem Klicken **gesperrt** wird:
 *
 * - Im Leer-Zustand zeigt sie ein Eingabefeld mit Vorschlagsliste.
 * - Nach der Auswahl wird der Wert als Chip dargestellt – nicht editierbar,
 *   nur über das ×-Icon vollständig zu löschen.
 * - Nach dem Löschen kehrt sie automatisch in den Eingabe-Modus zurück.
 *
 * Damit ist gewährleistet, dass der User nicht versehentlich an einem bereits
 * ausgewählten Wert weiter tippt – er muss ihn erst entfernen, dann neu suchen.
 */
function LockedAutocompleteImpl<T>(
  {
    label,
    hint,
    placeholder,
    value,
    onChange,
    onQueryChange,
    loadOptions,
    minChars = 0,
    debounceMs = 200,
    emptyText = "Keine Treffer",
    className,
    required,
    error,
    disabled,
    disabledHint,
    autoFocus = false,
    reloadKey,
    keepOpenOnSelect = false,
    allowFreeText = false,
    createFreeTextOption,
    allowEditLocked,
  }: LockedAutocompleteProps<T>,
  ref: React.Ref<LockedAutocompleteHandle>,
) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const clearBtnRef = useRef<HTMLButtonElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AutocompleteOption<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isLocked = !!value;
  const trimmed = query.trim();
  const canSearch = trimmed.length >= minChars;

  const noOptions = useMemo(
    () => !loading && options.length === 0 && canSearch && open,
    [loading, options.length, canSearch, open],
  );

  function buildFreeTextOption(text: string): AutocompleteOption<T> {
    const q = text.trim();
    if (createFreeTextOption) return createFreeTextOption(q);
    return { key: `free:${q}`, label: q, value: q as T };
  }

  function commitFreeText(text: string) {
    const q = text.trim();
    if (!allowFreeText || !q || isLocked) return;
    handleSelect(buildFreeTextOption(q));
  }

  // Position für das per Portal gerenderte Dropdown – sorgt dafür, dass die
  // Vorschläge nicht an Modal-/Container-Kanten abgeschnitten werden.
  const panelOpen = open && !isLocked && !disabled;
  const panelHeightEstimate = useMemo(
    () =>
      estimateAutocompletePanelHeight({
        optionCount: options.length,
        loading,
        empty: noOptions,
        hasDescription: options.some((opt) => Boolean(opt.description)),
        maxHeightCap: 320,
      }),
    [loading, noOptions, options],
  );
  const floating = useFloatingPanel(inputRef, panelOpen, {
    gap: 4,
    maxHeightCap: 320,
    minBelow: 180,
    contentHeightEstimate: panelHeightEstimate,
  });

  // Bei Flip (Liste oberhalb): bester Treffer nah an der Eingabe halten.
  const optionEntries = useMemo(() => {
    const entries = options.map((opt, idx) => ({ opt, idx }));
    return floating?.flipped ? [...entries].reverse() : entries;
  }, [options, floating?.flipped]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || !floating?.flipped) return;
    el.scrollTop = el.scrollHeight;
  }, [floating?.flipped, optionEntries, loading]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (activeIndex < 0 || !list) return;
    const activeEl = list.querySelector<HTMLElement>(`[data-ac-idx="${activeIndex}"]`);
    if (!activeEl) return;
    // Nur die Liste scrollen – nicht die Seite (wichtig auf Mobil).
    const top = activeEl.offsetTop;
    const bottom = top + activeEl.offsetHeight;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [activeIndex]);

  useImperativeHandle(
    ref,
    () => ({
      clear: () => {
        if (disabled) return;
        onChange(null);
        setQuery("");
        onQueryChange?.("");
        setOptions([]);
        setActiveIndex(-1);
        // Fokus zurück ins Suchfeld, wenn der Reset bewusst ausgelöst wurde.
        window.setTimeout(() => inputRef.current?.focus(), 0);
      },
      focus: () => {
        if (isLocked) {
          clearBtnRef.current?.focus();
        } else {
          inputRef.current?.focus();
        }
      },
      getPendingQuery: () => query,
    }),
    [disabled, isLocked, onChange, onQueryChange, query],
  );

  useEffect(() => {
    if (autoFocus && !isLocked) {
      inputRef.current?.focus();
    }
  }, [autoFocus, isLocked]);

  // Im gesperrten Zustand wollen wir das Listing nicht offen haben.
  useEffect(() => {
    if (isLocked) {
      setOpen(false);
      setQuery("");
      setOptions([]);
    }
  }, [isLocked]);

  useEffect(() => {
    if (!open || isLocked) return;
    if (!canSearch) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const list = await loadOptions(trimmed);
        if (!cancelled) {
          setOptions(list || []);
          setActiveIndex(list && list.length ? 0 : -1);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [trimmed, open, canSearch, isLocked, loadOptions, debounceMs, reloadKey]);

  function handleSelect(option: AutocompleteOption<T>) {
    onChange(option);
    onQueryChange?.("");
    if (keepOpenOnSelect) {
      setQuery("");
      setOpen(true);
      setActiveIndex(-1);
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    setOpen(false);
    setOptions([]);
    setQuery("");
  }

  function handleClear() {
    if (disabled) return;
    onChange(null);
    setQuery("");
    onQueryChange?.("");
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleEditLocked() {
    if (!value || disabled || !allowEditLocked?.(value)) return;
    setQuery(value.label);
    onQueryChange?.(value.label);
    onChange(null);
    setOpen(false);
    setOptions([]);
    setActiveIndex(-1);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  const canEditLocked = Boolean(value && allowEditLocked?.(value));

  const statusRow = loading ? (
    <li key="status" className="flex items-center gap-2 px-3 py-2 text-label-tertiary">
      <Spinner className="h-3.5 w-3.5" />
      Suche …
    </li>
  ) : noOptions ? (
    <li key="status" className="px-3 py-2 text-label-tertiary">
      {emptyText}
    </li>
  ) : null;
  const optionRows = optionEntries.map(({ opt, idx }) => (
    <li
      key={opt.key}
      data-ac-idx={idx}
      role="option"
      aria-selected={idx === activeIndex}
      onMouseEnter={() => setActiveIndex(idx)}
      onMouseDown={(e) => {
        e.preventDefault();
        handleSelect(opt);
      }}
      className={clsx(
        "cursor-pointer px-3 py-1.5",
        idx === activeIndex ? "bg-accent text-label-on-accent" : "text-label",
      )}
    >
      <div className="font-medium">{opt.label}</div>
      {opt.description && (
        <div
          className={clsx(
            "text-xs",
            idx === activeIndex ? "text-label-on-accent/80" : "text-label-secondary",
          )}
        >
          {opt.description}
        </div>
      )}
    </li>
  ));
  // Flip: bester Treffer / Status nah an der Eingabe (unten im Panel).
  const listContent = floating?.flipped ? (
    <>
      {optionRows}
      {statusRow}
    </>
  ) : (
    <>
      {statusRow}
      {optionRows}
    </>
  );

  return (
    <div className={clsx("space-y-1", className)}>
      {label && (
        <label className="eh-label" htmlFor={id}>
          {label}
          {required ? <span className="ml-1 text-status-red">*</span> : null}
        </label>
      )}

      {isLocked && value ? (
        // ─── Chip-Ansicht: nur löschbar, nicht editierbar ───
        <div
          className={clsx(
            "flex items-center justify-between gap-2 rounded-ios border bg-fill-tertiary px-3 py-2 text-sm",
            error ? "border-status-red" : "border-separator-opaque",
            disabled && "opacity-60",
          )}
          aria-readonly
        >
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-label">{value.label}</div>
            {value.description && (
              <div className="truncate text-xs text-label-secondary">
                {value.description}
              </div>
            )}
          </div>
          {!disabled && (
            <div className="flex shrink-0 items-center gap-0.5">
              {canEditLocked ? (
                <button
                  type="button"
                  onClick={handleEditLocked}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-label-secondary transition hover:bg-fill hover:text-label focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
                  aria-label="Bearbeiten"
                  title="Bearbeiten"
                >
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-4 w-4">
                    <path
                      d="M13.5 3.5l3 3L7 16H4v-3L13.5 3.5z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : null}
              <button
                ref={clearBtnRef}
                type="button"
                onClick={handleClear}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-label-secondary transition hover:bg-fill hover:text-label focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
                aria-label="Auswahl entfernen"
                title="Auswahl entfernen"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden
                  className="h-4 w-4"
                >
                  <path
                    d="M6 6l8 8M14 6l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            id={id}
            type="text"
            autoComplete="off"
            disabled={disabled}
            className={clsx(
              "eh-input",
              loading && "pr-9",
              error && "border-status-red focus:border-status-red",
              disabled && "cursor-not-allowed opacity-60",
            )}
            placeholder={placeholder}
            value={query}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              if (closeTimer.current) window.clearTimeout(closeTimer.current);
              closeTimer.current = window.setTimeout(() => {
                setOpen(false);
                if (allowFreeText && trimmed) {
                  commitFreeText(trimmed);
                }
              }, 120);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              onQueryChange?.(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (!open) {
                if (e.key === "ArrowDown") {
                  setOpen(true);
                  e.preventDefault();
                }
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((idx) => (idx + 1) % Math.max(options.length, 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((idx) =>
                  idx <= 0 ? options.length - 1 : idx - 1,
                );
              } else if (e.key === "Enter") {
                if (options[activeIndex]) {
                  e.preventDefault();
                  handleSelect(options[activeIndex]);
                } else if (allowFreeText && trimmed) {
                  e.preventDefault();
                  commitFreeText(trimmed);
                }
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={`${id}-listbox`}
            aria-required={required || undefined}
            aria-busy={loading || undefined}
          />
          {loading ? (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-label-tertiary"
            >
              <Spinner className="h-4 w-4" />
            </span>
          ) : null}
          {panelOpen &&
            floating &&
            (loading || options.length > 0 || noOptions) &&
            typeof document !== "undefined" &&
            createPortal(
              <ul
                ref={listRef}
                id={`${id}-listbox`}
                role="listbox"
                className="z-[1000] overflow-auto rounded-lg border border-separator bg-card py-1 text-sm shadow-ios-md"
                style={floatingPanelStyle(floating)}
                // mousedown nicht in den Input-Blur durchschlagen lassen –
                // sonst schließt das Listing, bevor der Klick auf einer
                // Option ankommt.
                onMouseDown={(e) => e.preventDefault()}
              >
                {listContent}
              </ul>,
              document.body,
            )}
        </div>
      )}

      {disabled && disabledHint ? (
        <p className="text-xs text-label-tertiary">{disabledHint}</p>
      ) : (
        <>
          {hint && !error && <p className="text-xs text-label-secondary">{hint}</p>}
          {error && <p className="text-xs text-status-red">{error}</p>}
        </>
      )}
    </div>
  );
}

/**
 * Generischer Wrapper, der die Generic-Type-Ableitung beibehält.
 * (Standard-`forwardRef` schluckt Generics.)
 */
export const LockedAutocomplete = forwardRef(LockedAutocompleteImpl) as <T>(
  props: LockedAutocompleteProps<T> & {
    ref?: React.Ref<LockedAutocompleteHandle>;
  },
) => ReturnType<typeof LockedAutocompleteImpl>;
